/**
 * Display helpers for nursery receivables.
 *
 * Amounts arrive as numbers, but a JSON aggregate or an older payload can
 * carry a string ('1100.00'), and `'0' > 0` style checks then go wrong. Every
 * amount on screen goes through here.
 */

import type { PaymentState, PaymentTerms } from '../types/nurseryAccounts';

export const num = (v: number | string | null | undefined): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/** "$1,234.50". A negative reads "−$12.00". */
export const money = (v: number | string | null | undefined): string => {
  const n = num(v);
  const body = Math.abs(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '−' : ''}$${body}`;
};

/** A rate without trailing zeros: 21, 2.5. */
export const pct = (v: number | string | null | undefined): string => `${Number(num(v).toFixed(3))}%`;

export const termsLabel = (terms: PaymentTerms | null | undefined): string => {
  if (!terms) return 'No terms set';
  if (terms.type === 'eom_days') return terms.days ? `${terms.days} days after end of month` : 'End of month';
  return terms.days ? `${terms.days} days` : 'Due on invoice';
};

export const PAYMENT_STATE_LABELS: Record<PaymentState, string> = {
  draft: 'Draft',
  open: 'Awaiting payment',
  part_paid: 'Part paid',
  overdue: 'Overdue',
  paid: 'Paid',
  void: 'Void',
};

/** Tailwind classes for a payment-state badge. */
export const PAYMENT_STATE_CLASSES: Record<PaymentState, string> = {
  draft: 'bg-neutral-100 text-neutral-700',
  open: 'bg-blue-50 text-blue-800',
  part_paid: 'bg-amber-50 text-amber-800',
  overdue: 'bg-red-50 text-red-700',
  paid: 'bg-green-50 text-green-800',
  void: 'bg-neutral-100 text-neutral-500 line-through',
};

/** Round to cents the way the server does. */
export const round2 = (n: number): number => Math.sign(n) * Math.round((Math.abs(n) + Number.EPSILON) * 100) / 100;
